import { Form } from "antd";
import Search from "antd/es/input/Search";
import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Product, SortOrder } from "tm-shared/types";
import { buildRoutePath, isPointDistribution } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { useCatalog } from "@/domains/product";
import {
    useAppDispatch,
    useAppSelector,
    useAsyncAction,
    useCheckoutSelector,
    useInfiniteScroll,
    usePointPriceListSelector,
    usePointSelector,
    useQueryFilter,
    useStorageInventorySelector,
} from "@/hooks";
import { CheckoutProductCard } from "@/pages/Checkout/CheckoutAddProducts/components/CheckoutProductCard/CheckoutProductCard";
import { useSaveOrder } from "@/pages/Checkout/hooks";
import { useOfflineFilteredProducts } from "@/pages/WorkPlan/Tasks/components/OfflineFilterDrawer";
import { getFilterCount } from "@/pages/WorkPlan/Tasks/utils";
import { Header, MainContainer } from "@/shared/layout";
import { HeaderFilterButton } from "@/shared/navigation";
import { Meta } from "@/shared/seo";
import { BaseButton } from "@/shared/ui";
import {
    setProductsSelectedFilters,
    setProductsSortOrder,
    setProductsSortTag,
    setUpdateProductBalancesQuery,
} from "@/store/slices/ui";

import { OfflineFilterDrawer } from "../../WorkPlan/Tasks/components/OfflineFilterDrawer";

interface LocalProduct {
    barcode: string;
    name: string;
    price: number;
    count: number;
}

export const CheckoutAddProducts = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { pointId = "", orderId = "" } = useParams();
    const { handleSaveOrder } = useSaveOrder();

    // --- UI state ---

    const { selectedFilters, hideZeroProducts, searchQuery } = useAppSelector(
        (state) => ({
            selectedFilters: state.ui.productsFilter.selectedFilters,
            hideZeroProducts: state.ui.productsFilter.hideZeroProducts,
            searchQuery: state.ui.updateProductBalances.query,
        }),
        shallowEqual,
    );

    // --- Point context ---
    const currentPoint = usePointSelector(pointId);

    const storageId =
        currentPoint && isPointDistribution(currentPoint)
            ? currentPoint.typeContext.storage.id
            : "";

    const currentInventoryList = useStorageInventorySelector(storageId) ?? {};

    const currentPriceList = usePointPriceListSelector(pointId);

    // --- Offline data ---
    const ignoreRemainingsOrders = useAppSelector(
        (state) => state.offline.ignoreRemainingsOrders,
    );

    // --- Catalog ---
    const { products } = useCatalog();

    // --- Checkout ---
    const checkoutData = useCheckoutSelector(orderId);

    const [open, setOpen] = useState(false);
    const [localProducts, setLocalProducts] = useState<
        Record<string, LocalProduct>
    >(() => checkoutData?.products ?? {});

    const pageTitle = "Додати товар";

    const { sortedProducts } = useOfflineFilteredProducts({
        products,
        inventoryList: currentInventoryList,
    });

    const searchFilteredProducts = useQueryFilter<Product>(
        sortedProducts,
        searchQuery,
        [
            (p) => p?.name,
            (p) => p?.barcode,
            (p) => p?.params?.description?.value,
            (p) => p?.params?.capacity?.value,
        ],
    );

    const filterCount = useMemo(
        () => getFilterCount(selectedFilters),
        [selectedFilters],
    );

    const hasChanges = useMemo(
        () => !isEqual(localProducts, checkoutData?.products ?? {}),
        [localProducts, checkoutData?.products],
    );

    const showDrawer = useCallback(() => setOpen(true), []);

    const handleSearch = useCallback(
        (evt: React.ChangeEvent<HTMLInputElement>) =>
            dispatch(setUpdateProductBalancesQuery(evt.target.value)),
        [dispatch],
    );

    const handleAddProductLocal = useCallback(
        (barcode: string, name: string, price: number) => {
            setLocalProducts((prev) => ({
                ...prev,
                [barcode]: {
                    barcode,
                    name,
                    price,
                    count: 1,
                },
            }));
        },
        [],
    );

    const handleProductCountChangeLocal = useCallback(
        (barcode: string, newCount: number) => {
            setLocalProducts((prev) => {
                if (newCount === 0) {
                    const updated = { ...prev };
                    delete updated[barcode];
                    return updated;
                }

                const product = prev[barcode];
                if (!product) return prev;

                return {
                    ...prev,
                    [barcode]: {
                        ...product,
                        count: newCount,
                    },
                };
            });
        },
        [],
    );

    const [handleSaveChanges, saveLoading] = useAsyncAction(async () => {
        if (!orderId) return;

        await handleSaveOrder({
            orderId,
            products: localProducts,
            storageId,
        });

        navigate(buildRoutePath(ROUTES.CheckoutOrderDetails, { orderId }), {
            replace: true,
        });
    });

    useEffect(() => {
        dispatch(setProductsSelectedFilters({}));
        dispatch(setProductsSortOrder(SortOrder.Asc));
        dispatch(setProductsSortTag(null));
    }, [dispatch, products]);

    const { visibleItems, loadMore } = useInfiniteScroll<Product>(
        searchFilteredProducts,
    );
    const lastIndex = visibleItems.length - 1;

    return (
        <>
            <Meta title={pageTitle} />

            <Header
                title={pageTitle}
                back
                needConfirm={hasChanges}
                rightActions={
                    <HeaderFilterButton
                        filterCount={filterCount}
                        onClick={showDrawer}
                        active={hideZeroProducts}
                    />
                }
            />

            <MainContainer>
                <Search
                    placeholder="Пошук"
                    defaultValue={searchQuery}
                    onChange={handleSearch}
                    allowClear
                    size="large"
                    style={{ marginBottom: 8 }}
                />

                <div style={{ height: "100%", overflowY: "auto" }}>
                    <Form>
                        {visibleItems.map((item, i) => {
                            const { barcode, name } = item;
                            const itemsLeft = currentInventoryList?.[barcode];
                            const price = currentPriceList?.[barcode]?.price;

                            const localProduct = localProducts[barcode];
                            const checkout = localProduct
                                ? {
                                      barcode: localProduct.barcode,
                                      name: localProduct.name,
                                      count: localProduct.count,
                                      price: localProduct.price,
                                      pointId: pointId!,
                                      apiCount:
                                          checkoutData?.products?.[barcode]
                                              ?.apiCount ?? 0,
                                  }
                                : undefined;

                            return (
                                <CheckoutProductCard
                                    key={barcode}
                                    barcode={barcode}
                                    checkout={checkout}
                                    name={name}
                                    itemsLeft={itemsLeft}
                                    price={price ?? 0}
                                    orderId={orderId!}
                                    ignoreRemainingsOrders={
                                        ignoreRemainingsOrders
                                    }
                                    isLast={i === lastIndex}
                                    onView={loadMore}
                                    onAddProductLocal={handleAddProductLocal}
                                    onProductCountChangeLocal={
                                        handleProductCountChangeLocal
                                    }
                                />
                            );
                        })}
                    </Form>
                </div>

                {hasChanges && (
                    <BaseButton
                        onClick={handleSaveChanges}
                        type="primary"
                        style={{ marginTop: 8 }}
                        disabled={!hasChanges}
                        loading={saveLoading}
                    >
                        Зберегти зміни
                    </BaseButton>
                )}
            </MainContainer>

            <OfflineFilterDrawer
                open={open}
                setOpen={setOpen}
                inventoryList={currentInventoryList}
                products={products}
            />
        </>
    );
};
